import { useRef, useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle2, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useSEO } from "../lib/seo";
import { Reveal } from "../lib/reveal";
import { COMPANY, PROJECT_TYPES, BUDGETS } from "../data/content";

const INPUT =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm text-bati-ink outline-none transition-colors duration-300 placeholder:text-bati-ink/35 focus:border-bati-sky";
const LABEL = "mb-2 block text-[11px] font-bold uppercase tracking-[0.14em] text-bati-ink/70";

const ContactPage = () => {
  useSEO(
    "Demander un devis — BÂTI 360 | Rénovation Paris & Île-de-France",
    "Décrivez votre projet de rénovation à Paris ou en Île-de-France : BÂTI 360 étudie votre demande et vous recontacte pour organiser une première visite technique."
  );

  const formStart = useRef(Date.now() / 1000);
  const [form, setForm] = useState({
    nom: "", prenom: "", telephone: "", email: "", adresse: "", ville: "",
    type_projet: "", surface: "", budget: "", date_demarrage: "", description: "",
  });
  const [photos, setPhotos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState("idle");

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("form_ts", String(formStart.current));
    fd.append("site_web", e.target.site_web?.value || "");
    photos.slice(0, 6).forEach((f) => fd.append("photos", f));
    plans.slice(0, 4).forEach((f) => fd.append("plans", f));
    try {
      await api.post("/quote-requests", fd);
      setStatus("success");
      toast.success("Votre demande a bien été envoyée.");
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="pt-20 md:pt-24">
      <header className="border-b border-black/10 bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="mb-4 flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.22em] text-bati-sky">
              <span className="h-px w-10 bg-bati-sky" /> Contact & devis
            </p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-bati-ink sm:text-5xl lg:text-6xl">
              Parlez-nous de votre projet
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-bati-ink/60">
              Décrivez votre projet en quelques lignes. Nous étudions chaque demande et vous recontactons pour organiser une première visite technique.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[1fr_360px] lg:px-8">
          <div>
            {status === "success" ? (
              <div data-testid="form-success-message" className="flex flex-col items-start gap-5 border border-black/10 bg-bati-mist p-10 lg:p-14">
                <CheckCircle2 className="h-12 w-12 text-bati-blue" strokeWidth={1.5} />
                <h2 className="font-display text-3xl font-extrabold tracking-tight text-bati-ink">Merci pour votre demande.</h2>
                <p className="max-w-lg text-base leading-relaxed text-bati-ink/65">
                  Notre équipe reviendra vers vous afin d'étudier votre projet. Si votre chantier est urgent, vous pouvez aussi nous joindre directement au{" "}
                  <a href={COMPANY.phoneHref} className="font-semibold text-bati-blue hover:text-bati-sky">{COMPANY.phone}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="devis-form" className="border border-black/10 bg-white p-6 sm:p-10">
                <input type="text" name="site_web" tabIndex={-1} autoComplete="off" className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nom" className={LABEL}>Nom *</label>
                    <input id="nom" name="nom" required value={form.nom} onChange={update} className={INPUT} data-testid="input-nom" placeholder="Votre nom" />
                  </div>
                  <div>
                    <label htmlFor="prenom" className={LABEL}>Prénom *</label>
                    <input id="prenom" name="prenom" required value={form.prenom} onChange={update} className={INPUT} data-testid="input-prenom" placeholder="Votre prénom" />
                  </div>
                  <div>
                    <label htmlFor="telephone" className={LABEL}>Téléphone *</label>
                    <input id="telephone" name="telephone" type="tel" required value={form.telephone} onChange={update} className={INPUT} data-testid="input-telephone" placeholder="06 00 00 00 00" />
                  </div>
                  <div>
                    <label htmlFor="email" className={LABEL}>E-mail *</label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={update} className={INPUT} data-testid="input-email" placeholder="vous@exemple.fr" />
                  </div>
                  <div>
                    <label htmlFor="adresse" className={LABEL}>Adresse du chantier</label>
                    <input id="adresse" name="adresse" value={form.adresse} onChange={update} className={INPUT} data-testid="input-adresse" placeholder="N° et rue" />
                  </div>
                  <div>
                    <label htmlFor="ville" className={LABEL}>Ville</label>
                    <input id="ville" name="ville" value={form.ville} onChange={update} className={INPUT} data-testid="input-ville" placeholder="Paris 16e, Meaux…" />
                  </div>
                  <div>
                    <label htmlFor="type_projet" className={LABEL}>Type de projet</label>
                    <select id="type_projet" name="type_projet" value={form.type_projet} onChange={update} className={INPUT} data-testid="select-type-projet">
                      <option value="">Sélectionnez…</option>
                      {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="surface" className={LABEL}>Surface approximative</label>
                    <input id="surface" name="surface" value={form.surface} onChange={update} className={INPUT} data-testid="input-surface" placeholder="ex. 90 m²" />
                  </div>
                  <div>
                    <label htmlFor="budget" className={LABEL}>Budget estimatif</label>
                    <select id="budget" name="budget" value={form.budget} onChange={update} className={INPUT} data-testid="select-budget">
                      <option value="">Sélectionnez…</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="date_demarrage" className={LABEL}>Démarrage souhaité</label>
                    <input id="date_demarrage" name="date_demarrage" type="month" value={form.date_demarrage} onChange={update} className={INPUT} data-testid="input-date-demarrage" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="description" className={LABEL}>Description du projet</label>
                    <textarea id="description" name="description" rows={5} value={form.description} onChange={update} className={`${INPUT} resize-y`} data-testid="input-description" placeholder="Décrivez les travaux envisagés, l'état actuel du bien, vos attentes…" />
                  </div>
                  <div>
                    <span className={LABEL}>Photos du bien</span>
                    <label className="flex cursor-pointer items-center gap-3 border border-dashed border-black/20 px-4 py-4 text-sm text-bati-ink/55 transition-colors hover:border-bati-sky hover:text-bati-blue" data-testid="upload-photos-label">
                      <CloudUpload className="h-5 w-5 shrink-0 text-bati-sky" />
                      {photos.length ? `${photos.length} photo(s) sélectionnée(s)` : "Ajouter des photos (max. 6)"}
                      <input type="file" accept="image/*" multiple className="hidden" data-testid="upload-photos" onChange={(e) => setPhotos(Array.from(e.target.files || []))} />
                    </label>
                  </div>
                  <div>
                    <span className={LABEL}>Plans ou PDF</span>
                    <label className="flex cursor-pointer items-center gap-3 border border-dashed border-black/20 px-4 py-4 text-sm text-bati-ink/55 transition-colors hover:border-bati-sky hover:text-bati-blue" data-testid="upload-plans-label">
                      <CloudUpload className="h-5 w-5 shrink-0 text-bati-sky" />
                      {plans.length ? `${plans.length} fichier(s) sélectionné(s)` : "Ajouter des plans (max. 4)"}
                      <input type="file" accept=".pdf,image/*" multiple className="hidden" data-testid="upload-plans" onChange={(e) => setPlans(Array.from(e.target.files || []))} />
                    </label>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="submit-devis-button"
                  className="mt-8 flex items-center gap-3 bg-bati-blue px-10 py-4 text-[13px] font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-bati-sky disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {status === "sending" ? "Envoi en cours…" : "Envoyer ma demande"}
                </button>
                <p className="mt-4 text-xs leading-relaxed text-bati-ink/45">
                  Vos données ne sont utilisées que pour traiter votre demande. Consultez notre politique de confidentialité.
                </p>
              </form>
            )}
          </div>

          <aside className="space-y-8">
            <Reveal>
              <div className="border border-black/10 bg-bati-mist p-8">
                <h2 className="font-display text-sm font-bold uppercase tracking-[0.16em] text-bati-blue">Coordonnées</h2>
                <ul className="mt-6 space-y-5 text-sm">
                  <li>
                    <a href={COMPANY.phoneHref} data-testid="contact-phone" className="flex items-center gap-3 font-display text-xl font-bold text-bati-ink transition-colors hover:text-bati-sky">
                      <Phone className="h-5 w-5 text-bati-sky" /> {COMPANY.phone}
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-3 text-bati-ink/75 transition-colors hover:text-bati-sky">
                      <Mail className="h-5 w-5 text-bati-sky" /> {COMPANY.email}
                    </a>
                  </li>
                  <li className="flex items-start gap-3 text-bati-ink/75">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-bati-sky" />
                    <span>{COMPANY.address}<br />{COMPANY.city}</span>
                  </li>
                </ul>
                <p className="mt-6 border-t border-black/10 pt-5 text-xs leading-relaxed text-bati-ink/55">
                  Zone d'intervention : {COMPANY.zone}.<br />Assurance décennale — entreprise basée en Île-de-France.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden border border-black/10">
                <iframe
                  title="BÂTI 360 — Avenue Roland Moreno, 77124 Chauconin-Neufmontiers"
                  src="https://www.google.com/maps?q=Avenue%20Roland%20Moreno%2C%2077124%20Chauconin-Neufmontiers%2C%20France&output=embed"
                  className="h-[340px] w-full border-0"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
